import queryClient from "api/queryClient.ts";
import {
    useGetOutgoingPostbackQuery,
    useUpdateOutgoingPostbackMutation,
} from "api/TenantSlug/OutgoingPostback/hooks.ts";
import { ErrorsChartSection } from "components/Forms/ConnectionCreatorGeneralFormSections/ErrorsChartSection.tsx";
import { InformationSection } from "components/Forms/ConnectionCreatorGeneralFormSections/InformationSection.tsx";
import { OutgoingPostbackGeneralFormSection } from "components/Forms/ConnectionCreatorGeneralFormSections/OutgoingPostbackGeneralFormSection/OutgoingPostbackGeneralFormSection.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { SectionContentWrapper } from "components/UI/SectionContentWrapper";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { MULTISELECT_ALL_VALUE } from "localConstants.ts";
import { FC, useEffect } from "react";
import { toast } from "react-toastify";
import { FormId } from "types/models.ts";

import { formSchema, OutgoingPostbackFormData } from "./formSchema.ts";

import OutgoingPostbackData = App.Data.OutgoingPostbackData;

interface TrackingToolToGeneralFormProps {
    postback: OutgoingPostbackData;
}

const TrackingToolToGeneralFormContent: FC<TrackingToolToGeneralFormProps> = (props) => {
    const generalFormUtils = useGeneralForm<OutgoingPostbackFormData>();
    const { reset } = generalFormUtils;

    // Reset form when postback changes (after refetch)
    useEffect(() => {
        if (props.postback) {
            reset(transformPostbackToFormData(props.postback));
        }
    }, [props.postback, reset]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="flex flex-col gap-y-6">
                <OutgoingPostbackGeneralFormSection outgoingPostback={props.postback} />
                {/*<SectionContentWrapper withBackground>
                    <CustomFieldsSection />
                </SectionContentWrapper>*/}
            </div>
            <div className="flex flex-col gap-y-6">
                <SectionContentWrapper withBackground>
                    <InformationSection />
                </SectionContentWrapper>
                <SectionContentWrapper withBackground>
                    <ErrorsChartSection />
                </SectionContentWrapper>
            </div>
        </div>
    );
};

export const TrackingToolToGeneralForm: FC<TrackingToolToGeneralFormProps> = ({ postback }) => {
    const updateOutgoingPostback = useUpdateOutgoingPostbackMutation(postback?.id || "");
    const { t } = usePaldockTranslation("forms.tracking-tool-to-general");
    const getOutgoingPostbackQuery = useGetOutgoingPostbackQuery(postback.id, {
        filter: {
            include:
                "types,sources,country,offers,excludedOffers,affiliates,advertisers,integrations,cascades,formStructures",
        },
    });

    if (!postback) return null;

    const handleMultiSelectValue = <T extends string | number>(
        arr: (T | typeof MULTISELECT_ALL_VALUE)[] = []
    ): Record<string, T> => {
        if (arr.includes(MULTISELECT_ALL_VALUE)) return {};
        return Object.fromEntries(arr.map((val) => [val, val])) as Record<string, T>;
    };

    const handleSubmit = (data: OutgoingPostbackFormData) => {
        const payload = {
            id: postback.id,
            ...data,

            types: handleMultiSelectValue(data.types),
            sources: handleMultiSelectValue(data.sources),
            offer_ids: handleMultiSelectValue(data.offer_ids),
            excluded_offer_ids: handleMultiSelectValue(data.excluded_offer_ids),
            country_id: data.country_id ?? null,
            affiliate_ids: handleMultiSelectValue(data.affiliate_ids),
            advertiser_ids: handleMultiSelectValue(data.advertiser_ids),
            integration_ids: handleMultiSelectValue(data.integration_ids),
            cascade_ids: handleMultiSelectValue(data.cascade_ids),
            form_structure_ids: data.form_structure_ids ?? [],
            payout_result: data.payout_result ?? null,
            payout_type: data.payout_type ?? null,
        };

        updateOutgoingPostback.mutate(payload, {
            onSuccess: () => {
                queryClient.refetchQueries({ queryKey: ["getOutgoingPostback", postback.id] });
                queryClient.refetchQueries({ queryKey: ["getOutgoingPostbacks"] });
                getOutgoingPostbackQuery.refetch();
                toast.success(t("messages.update_success"));
            },
            onError: () => {
                toast.error(t("messages.update_error"));
            },
        });
    };

    const transformedDefaultValues = transformPostbackToFormData(postback);

    return (
        <GeneralFormProvider<OutgoingPostbackFormData>
            schema={formSchema}
            id={FormId.TrackingToolToGeneralForm}
            defaultValues={transformedDefaultValues}
            onSubmit={handleSubmit}
            resetOnSubmit={false}
        >
            <TrackingToolToGeneralFormContent postback={postback} />
        </GeneralFormProvider>
    );
};

const transformPostbackToFormData = (postback: OutgoingPostbackData): OutgoingPostbackFormData => ({
    name: postback.name ?? "",
    description: postback.description ?? "",
    is_active: postback.is_active ?? false,
    use_new_connection_creator: postback.use_new_connection_creator ?? false,
    first_sequence_step_id: postback.first_sequence_step_id ?? null,
    trigger: postback.trigger,

    types: postback.types?.map((t) => (typeof t === "string" ? t : t.title)) ?? [],
    sources: postback.sources ? Object.values(postback.sources) : [],

    offer_ids: postback.offers?.length ? postback.offers.map((o) => o.id) : [MULTISELECT_ALL_VALUE],
    excluded_offer_ids: postback.excludedOffers?.map((offer) => offer.id) ?? [],
    country_id: postback.country_id ?? postback.country?.id ?? null,
    affiliate_ids: postback.affiliates?.length ? postback.affiliates.map((u) => u.id) : [MULTISELECT_ALL_VALUE],
    advertiser_ids: postback.advertisers?.length ? postback.advertisers.map((u) => u.id) : [MULTISELECT_ALL_VALUE],
    integration_ids: postback.integrations?.length
        ? postback.integrations.map((u) => u.id ?? "")
        : [MULTISELECT_ALL_VALUE], // TODO - why IntegrationData id parameter is possibly undefined?
    cascade_ids: postback.cascades?.length ? postback.cascades.map((u) => u.id) : [MULTISELECT_ALL_VALUE],
    form_structure_ids: postback.form_structures?.map((fs) => fs.id).filter((id): id is string => id !== null) ?? [],
    payout_result: postback.payout_result,
    payout_type: (postback.payout_type as OutgoingPostbackFormData["payout_type"]) ?? null,
    owner_id: postback.owner_id ?? undefined,
});
